import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { MultiFileInput } from "../ui";
import { CaseOutcomeFields, useCaseOutcome } from "../CaseOutcomeFields";
import { ITEM_OUTCOME_OPTIONS, type ProceedingScope } from "../../types";

/** Co „Zakończ" potrzebuje wiedzieć o pakiecie i jego sprawie — strona pakietu podaje
 *  `OfficialApplicationDetail`, zakładka Sprawy składa to z karty sprawy. */
export type CompleteTarget = {
  number: string;
  proceeding_info: {
    id: string;
    type_name: string;
    scope: ProceedingScope;
    /** Brak = sprawa otwarta (zakładka Sprawy otwiera modal tylko dla otwartych). */
    status?: string;
  } | null;
  /** Wniosek o KLAUZULĘ: „Zakończ” stawia stempel na decyzji (data klauzuli + skan) i
   *  odnotowuje powrót ORYGINAŁU decyzji — rozstrzygnięcie jest wtedy zawsze wymagane,
   *  także bez sprawy. */
  application_type?: string;
  decision_detail?: { sign: string } | null;
  decision_original_sent_at?: string | null;
};

export type CompleteValues = {
  outcome: string;
  resolved_parcels?: string[];
  note?: string;
  office_ref?: string;
  date_received?: string;
  files?: File[];
  final_at?: string;
  clause_file?: File;
  original_returned_at?: string;
};

const CLAUSE_OUTCOMES = new Set(["positive", "negative"]);

/**
 * „Zakończ" pakietu, którego sprawa jest jeszcze OTWARTA. Pakiet i sprawa to jedno
 * postępowanie, więc domykają się razem — a zamknięcie sprawy wymaga rozstrzygnięcia:
 * pozytywne zabezpiecza tytuły właścicieli w sprawie (podstawa „decyzja"), częściowe —
 * tylko właścicieli działek zaznaczonych jako uzgodnione, negatywne zostawia działki do
 * dalszego pozyskania. Tę decyzję podejmuje człowiek, nigdy automat — stąd modal zamiast
 * domyślnego „pozytywne". Środek okna (CaseOutcomeFields) jest ten sam co w „Zamknij
 * sprawę” w Korespondencji.
 * Odpowiedź urzędu (znak, data wpływu, skany) trafia do sprawy jako pozycja „odpowiedź"
 * zamykająca wątek — tak samo, jakby ktoś dodał ją ręcznie w Korespondencji.
 * Wniosek o klauzulę: pozytywne = zaświadczenie wpłynęło — backend oznacza decyzję jako
 * ostateczną (to, co robił ręczny stempel w rejestrze decyzji) i odnotowuje, że ORYGINAŁ
 * decyzji wrócił; negatywne = organ odmówił, decyzja wraca do „nieostatecznej".
 */
export function CompleteApplicationModal({ app, open, onClose, onConfirm, pending, error }: {
  app: CompleteTarget;
  open: boolean;
  onClose: () => void;
  onConfirm: (v: CompleteValues) => void;
  pending?: boolean;
  error?: string | null;
}) {
  const info = app.proceeding_info;
  const caseOpen = info != null && info.status !== "closed";
  const isClause = app.application_type === "klauzula";
  const today = new Date().toISOString().slice(0, 10);
  const resolution = useCaseOutcome("positive");
  const outcome = resolution.outcome;
  const [officeRef, setOfficeRef] = useState("");
  const [dateReceived, setDateReceived] = useState(today);
  const [files, setFiles] = useState<File[]>([]);
  // Klauzula: data klauzuli i powrotu oryginału domyślnie = data wpływu; „" = idź za nią.
  const [finalAt, setFinalAt] = useState("");
  const [returnedAt, setReturnedAt] = useState("");
  const [clauseFile, setClauseFile] = useState<File | null>(null);
  const outcomeOptions = isClause
    ? ITEM_OUTCOME_OPTIONS.filter((o) => CLAUSE_OUTCOMES.has(o.value))
    : ITEM_OUTCOME_OPTIONS;

  return (
    <Modal open={open} onClose={onClose} title={`Zakończ pakiet ${app.number}`} size="sm">
      <div className="space-y-3 text-sm">
        {isClause ? (
          <p>
            Zaświadczenie o ostateczności decyzji <span className="font-mono">{app.decision_detail?.sign}</span>{" "}
            wróciło z organu? <b>Pozytywne</b> = klauzula nadana: system oznaczy decyzję jako
            ostateczną w rejestrze decyzji (data + skan) i odnotuje powrót ORYGINAŁU.{" "}
            <b>Negatywne</b> = organ odmówił, decyzja wraca do „nieostatecznej”.
            {caseOpen && <> Sprawa „{info?.type_name}” zamknie się razem z pakietem.</>}
          </p>
        ) : (
          <p>
            Sprawa „{info?.type_name}” jest jeszcze otwarta. Zakończenie pakietu doda do niej
            pozycję „Odpowiedź” i zamknie ją ze wskazanym rozstrzygnięciem — tak jak przycisk
            „Zamknij sprawę” w Korespondencji.
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="section-label">Znak pisma urzędu</span>
            <input className="input-base mt-1" placeholder="Sygnatura odpowiedzi" value={officeRef}
              onChange={(e) => setOfficeRef(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Data wpływu</span>
            <DatePicker className="mt-1" value={dateReceived}
              max={today} onChange={setDateReceived} />
          </label>
        </div>
        <CaseOutcomeFields
          state={resolution}
          proceedingId={caseOpen ? info?.id : null}
          scope={info?.scope}
          outcomeOptions={outcomeOptions}
          noteLabel="Notatka zamknięcia / treść odpowiedzi"
          notePlaceholder={isClause
            ? "np. zaświadczenie nr … z dnia … (widoczna na liście spraw)"
            : "np. warunek postawiony w decyzji (widoczna na liście spraw i na mapie)"}
        >
          {isClause && outcome === "positive" && (
            <div className="grid sm:grid-cols-2 gap-3 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
              <label className="block">
                <span className="section-label">Data nadania klauzuli</span>
                <DatePicker className="mt-1" value={finalAt || dateReceived} max={today} onChange={setFinalAt} />
                <span className="block text-[10px] text-[var(--color-text-muted)]">
                  Z pieczęci na decyzji; domyślnie data wpływu.
                </span>
              </label>
              <div>
                <span className="section-label">Skan zaświadczenia / ostemplowanej decyzji</span>
                <FileDropZone className="mt-1" accept=".pdf,image/*"
                              files={clauseFile ? [clauseFile] : []}
                              onFilesChange={(fs) => setClauseFile(fs[0] ?? null)} />
              </div>
            </div>
          )}
          {isClause && (
            <label className="block sm:max-w-xs">
              <span className="section-label">Oryginał decyzji wrócił dnia</span>
              <DatePicker className="mt-1" value={returnedAt || dateReceived} max={today} onChange={setReturnedAt} />
              <span className="block text-[10px] text-[var(--color-text-muted)]">
                {app.decision_original_sent_at
                  ? `Wyszedł ${new Date(app.decision_original_sent_at).toLocaleDateString("pl-PL")}; domyślnie wraca z datą wpływu.`
                  : "Wysyłka nie odnotowała wyjścia oryginału — datę poprawisz w fazie „Zakres i adresat”."}
              </span>
            </label>
          )}
        </CaseOutcomeFields>
        <MultiFileInput files={files} onChange={setFiles}
          label={isClause ? "Inne skany odpowiedzi urzędu" : "Skany odpowiedzi urzędu"} />
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={pending}>Anuluj</Button>
          <Button
            size="sm"
            loading={pending}
            disabled={caseOpen && resolution.missingParcels}
            onClick={() =>
              onConfirm({
                ...resolution.values(),
                office_ref: officeRef.trim() || undefined,
                date_received: dateReceived || undefined,
                files: files.length > 0 ? files : undefined,
                final_at: isClause && outcome === "positive" ? (finalAt || dateReceived || undefined) : undefined,
                clause_file: isClause && outcome === "positive" ? (clauseFile ?? undefined) : undefined,
                original_returned_at: isClause ? (returnedAt || dateReceived || undefined) : undefined,
              })
            }
          >
            {caseOpen ? "Zakończ pakiet i zamknij sprawę" : "Zakończ pakiet"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
